import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { InlineCheckbox } from "../FieldForm";

interface CatalogTermsDraft {
  url?: string;
  text?: string;
  version?: number;
  title?: string;
  required?: boolean;
}

/**
 * `kind: "catalogTerms"` — `CatalogTerms` (`packages/core/legal/catalogTerms.ts`):
 * `url?`/`text?`/`version` (REQUIRED once the field is present at all)/`title?`/
 * `required?`. The FIRST edit to any sub-field auto-seeds `version: 1` if absent
 * (so an admin filling in "the terms URL" doesn't hit a surprise "version
 * required" parse error) — `parseCatalog`/`parseManifest` still enforce it for
 * real before Save.
 */
export function CatalogTermsEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const draft: CatalogTermsDraft = value && typeof value === "object" ? (value as CatalogTermsDraft) : {};
  const active = Object.keys(draft).length > 0;

  const setField = (patch: Partial<CatalogTermsDraft>) => {
    const merged: CatalogTermsDraft = { ...draft, ...patch, version: draft.version ?? patch.version ?? 1 };
    const cleaned: CatalogTermsDraft = {};
    if (merged.url) cleaned.url = merged.url;
    if (merged.text) cleaned.text = merged.text;
    if (merged.title) cleaned.title = merged.title;
    if (merged.required !== undefined) cleaned.required = merged.required;
    cleaned.version = merged.version;
    onChange(cleaned);
  };

  const clear = () => onChange(undefined);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="body2">{label}</Typography>
        {active ? (
          <Button size="small" color="error" onClick={clear}>
            Clear terms
          </Button>
        ) : (
          <Button size="small" variant="outlined" onClick={() => setField({})}>
            + Add terms
          </Button>
        )}
      </Box>
      {active ? (
        <Stack spacing={1}>
          <TextField
            size="small"
            fullWidth
            label="Title"
            value={draft.title ?? ""}
            onChange={(e) => setField({ title: e.target.value })}
          />
          <TextField
            size="small"
            fullWidth
            label="URL"
            value={draft.url ?? ""}
            onChange={(e) => setField({ url: e.target.value })}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Text"
            value={draft.text ?? ""}
            onChange={(e) => setField({ text: e.target.value })}
          />
          <TextField
            size="small"
            type="number"
            label="Version"
            value={draft.version ?? 1}
            onChange={(e) => setField({ version: e.target.value === "" ? 1 : Number(e.target.value) })}
            sx={{ maxWidth: 140 }}
          />
          <InlineCheckbox
            label="Required (blocks opening until accepted)"
            checked={draft.required === true}
            onChange={(v) => setField({ required: v })}
          />
        </Stack>
      ) : null}
    </Box>
  );
}
